import { parseVariantKey, variantTitle } from '@dca/shared';

import { ItemIcon } from './ItemIcon.tsx';
import { ItemTooltip } from './ItemTooltip.tsx';
import { labelFor, useVariantLabels } from '../lib/variants.tsx';

/**
 * An item, rendered the same way everywhere.
 *
 * Anything carrying a `#` suffix is a variant the server will not describe, so
 * it gets the in-game tooltip on hover - identified or not. Plain items render
 * as an icon and a name with no extra machinery.
 *
 * Labels come from one shared cache keyed by hash, so a page does not need its
 * own endpoint widened to show what an item is. The hash is globally unique,
 * so a label learned on one page is correct on every other.
 */
export function ItemLabel({
  itemKey,
  material,
  displayName,
  size = 22,
  onLabel,
  sub,
}: {
  itemKey: string;
  material: string;
  /** Falls back to the item key, which is what most endpoints send. */
  displayName?: string;
  size?: number;
  /** Offered inside the tooltip when the variant is unidentified. */
  onLabel?: () => void;
  sub?: React.ReactNode;
}) {
  const labels = useVariantLabels();
  const { hash } = parseVariantKey(itemKey);
  const label = labelFor(labels, itemKey);
  const name = label ? variantTitle(itemKey, label.enchantments) : (displayName ?? itemKey);

  const face = (
    <span className="item-label">
      <ItemIcon material={material} size={size} alt={name} />
      <span className="item-label-text">
        <span className="item-label-name">
          {name}
          {label?.verified && (
            <span className="verified-tick" title="Verified by the shop owner">
              {'\u2713'}
            </span>
          )}
        </span>
        {sub}
      </span>
    </span>
  );

  if (!hash) return face;

  return (
    <ItemTooltip
      itemKey={itemKey}
      material={material}
      title={name}
      enchantments={label?.enchantments ?? []}
      verified={label?.verified ?? false}
      confirmations={label?.confirmations ?? 0}
      onLabel={onLabel}
    >
      {face}
    </ItemTooltip>
  );
}
